/**
 * Derived facts about the corpus: where it came from, what it holds, how it
 * is kept, and what it cannot see.
 *
 * # Most of this is not on the page yet, and that is deliberate
 *
 * `FOUNDATION` is — it opens the data page. The nine arrays below it are not.
 * They were the whole of the first version of that page, which rendered them
 * as nine label/value lists and read exactly like the transcription it was.
 * The page has been rebuilt around one section per filter topic, and those
 * sections are being written one at a time — Region first — so these arrays
 * are drawn on as each topic arrives rather than all at once.
 *
 * So they are a quarry, not dead code. **Do not sweep them.** Every figure
 * cost a trip into the Go and Svelte sources to establish, and re-deriving
 * the 29 export columns in their delivery order or the 22 harmonised
 * functions would be the expensive half of writing the remaining eight
 * topics. See `content/filter-topics.ts`, which is where they land.
 *
 * # Why the facts were gathered
 *
 * The three product pages sell outcomes. Everything factual about the data
 * itself was living inside them — the 13 filter groups and the 29 export
 * columns were on the Intelligence page, and the filter catalogue was
 * rendered a second time on Plus so that page could stand alone. That is why
 * Intelligence ran to 3,643 words with 2,485 of them visible, against 852 on
 * Cleaning. Reference content was doing the job of a pitch.
 *
 * It is also what the SEO/GEO audit asked for: crawlable detail on industry
 * filters, region criteria, contact roles, firmographics and export formats.
 * One page about the data is a better target for that than the same facts
 * spread across three pages selling three things.
 *
 * # Everything here is derived
 *
 * Not written from the brief. The sources are the jobs and filters that
 * produce them; the 29 columns are `internal/orders/export.go` in the order
 * that file delivers them; the eight size bands are `SizeBandForExport`; the
 * verification rule is `VerifiedConstructed` in the same file; the 22
 * functions are `ainova-app`'s `utils/roleMapping`; the 18 legal forms are
 * `filters/legalforms.ts`; the limits are the app's own `explain` strings and
 * doc comments. So this page cannot claim a field or a filter that does not
 * exist, and when one changes the diff lands here.
 *
 * # The limits are published on purpose
 *
 * `LIMITS` is the section a competitor's page will not have. A corpus that
 * states what it cannot see is more use to somebody deciding than one that
 * implies it sees everything, and each of these is a specific, checkable
 * sentence — which is the kind a language model quotes rather than
 * paraphrases.
 *
 * # Temporary duplication
 *
 * Until the product pages are slimmed, the filter catalogue and the field
 * list exist both here and on Intelligence. That is deliberate — build the
 * destination before emptying the source — but it should not stay that way,
 * because two pages holding the same reference content compete with each
 * other.
 */

/**
 * The page's opening argument: we collect this ourselves, from public
 * sources, automatically — and that is what puts us on top of quality.
 *
 * # Only about us
 *
 * An earlier draft made the case by contrast: bought-in address stock is
 * older from day one, nobody can tell you where a single entry came from,
 * we do not purchase third-party lists. Every one of those sentences is
 * about somebody else, and a page that argues against an unnamed competitor
 * spends its opening paragraph on them. It also dates badly and invites the
 * reader to go and check the comparison.
 *
 * So nothing here describes anyone else's practice. Not "nicht eingekauft",
 * not "wir kaufen nichts zu", not "statt". The claims stand on their own:
 * this is how we do it, and this is what it lets us do.
 *
 * # Why these four and in this order
 *
 * They are not four features. They are one decision, its two consequences,
 * and the thing that makes all three checkable:
 *
 *     selbst gesammelt    → the quality is ours to measure
 *     öffentliche Quellen → the origin of every entry is traceable
 *     vollautomatisch     → the quality stays current over time
 *     protokolliert       → and all of that can be shown, per value
 *
 * The audit trail goes last on purpose. Alone it is a technical detail about
 * record-keeping; after the other three it is their evidence, and it answers
 * the question those three invite — "how would you know?". It is also the
 * one claim here a reader cannot verify from the outside, which is why it
 * says precisely what is kept (origin, time of collection, previous value)
 * rather than the word "Audit-Trail" and a promise.
 *
 * All four land on quality, because that is the payoff the section is for.
 * The fact alone is the uninteresting half: "wir suchen die Quellen selbst
 * aus" describes a workflow, and "darum können wir die Qualität messen" is
 * the reason a reader should care. It is also what licenses the rest of the
 * page — the filter sections can state exactly what each filter cannot see
 * because we know how every entry got there.
 *
 * # The German has to sound spoken
 *
 * A second draft got rewritten for this alone. It had collected every tell of
 * German written by somebody thinking in another language: nominal style
 * where a verb would do ("die Erhebung liegt bei uns", "macht die Qualität
 * kontrollierbar"), statistical register for an everyday act ("erheben" for
 * what is simply collecting), a metaphor nobody says out loud ("läuft als
 * Maschine"), and "obenauf sein" — which is colloquial, means roughly "in
 * good spirits", and was doing the work of "we are on top of quality".
 *
 * The rule for anything added here: say it with a verb, in the order a person
 * would say it, and read it aloud before committing. "Wir suchen die Quellen
 * selbst aus, lesen sie aus und führen sie laufend nach" is three verbs and
 * survives being spoken. "Der ganze Bestand entsteht bei uns" is one noun
 * doing the work of all three and does not.
 *
 * # Not written from the brief
 *
 * The three claims are the ones `SOURCES` and `FRESHNESS` below already
 * support: six named public collectors, and a corpus that is re-read on a
 * schedule. This section is their summary, which is why it opens the page and
 * they appear later as the detail.
 */
export const FOUNDATION = {
  heading: 'Von uns erhoben, täglich gepflegt.',
  lead: 'Wir sammeln jede Angabe selbst und halten sie laufend aktuell. Darum wissen wir bei jedem Wert, woher er stammt und seit wann er gilt. Alle Kriterien auf dieser Seite lassen sich frei kombinieren.',
  pillars: [
    {
      icon: 'shield-check',
      title: 'Selbst gesammelt',
      body: 'Jede Angabe stammt aus Quellen, die wir selbst aufgebaut haben und pflegen. So können wir die Qualität messen und gezielt verbessern.'
    },
    {
      icon: 'document',
      title: 'Öffentliche Quellen',
      body: 'Handelsregister, Firmenwebsites, amtliche Verzeichnisse, Stelleninserate und DNS-Einträge. Alles öffentlich einsehbar und jederzeit nachprüfbar.'
    },
    {
      icon: 'clock',
      title: 'Vollautomatisch',
      body: 'Unsere Systeme lesen die Quellen fortlaufend neu und gleichen jede Änderung ab. Ihre Daten bleiben aktuell, ohne dass jemand von Hand nachträgt.'
    },
    {
      icon: 'search',
      title: 'Lückenlos dokumentiert',
      body: 'Für jeden Wert halten wir fest, woher er stammt, wann wir ihn erfasst haben und was vorher galt.'
    }
  ]
};

/** Where the corpus comes from. Every entry maps to a real collector. */
export const SOURCES = [
  {
    label: 'Handelsregister',
    text: 'Firmenname, Sitz, Gründungsdatum, Rechtsform, einbezahltes Kapital, Revisionsstelle und Revisionsverzicht — sowie Kapitalerhöhungen als datierte Veränderung.'
  },
  {
    label: 'Firmenwebseiten',
    text: 'Was ein Unternehmen heute anbietet, gelesen aus dem eigenen Webauftritt und zu einer Zusammenfassung verdichtet. Daraus stammen auch die erkannten Technologien und Hinweise auf offene Lehrstellen.'
  },
  {
    label: 'Öffentliche Verzeichnisse',
    text: 'Adressen, Telefonnummern und Social-Media-Profile aus öffentlich zugänglichen Quellen.'
  },
  {
    label: 'Stellenanzeigen',
    text: 'Öffentlich ausgeschriebene Stellen, als Hinweis darauf, dass eine Firma gerade investiert. Personalvermittler sind ausgenommen, weil ihre Anzeigen für nicht genannte Kundenfirmen gelten.'
  },
  {
    label: 'DNS',
    text: 'Der E-Mail-Versand einer Domain — DMARC- und SPF-Einträge. Die einzige Quelle, bei der sich das Fehlen einer Sache zuverlässig feststellen lässt.'
  },
  {
    label: 'E-Mail-Verifikation',
    text: 'Adressen werden gegen den Mailserver geprüft. Übernommen wird nur, wo ein Postfach tatsächlich gefunden wurde — nicht, wo ein Server jede Adresse annimmt.'
  }
];

/** What a record holds. The 29 export columns, grouped. */
export const RECORD_FIELDS = [
  {
    label: 'Identität',
    value:
      'Ainova ID, Unternehmung, Sitz, Gründungsdatum, Rechtsform, Grössenklasse, Segment, Branche'
  },
  { label: 'Adresse', value: 'Postfach, c/o, Strasse, Hausnummer, Zusatz, PLZ, Ort, Land' },
  {
    label: 'Erreichbarkeit der Firma',
    value: 'Webseite, E-Mail, Telefon, LinkedIn, Facebook, Instagram, Twitter'
  },
  {
    label: 'Ansprechpartner',
    value: 'Geschlecht, Vorname, Nachname, E-Mail, Telefon, Rolle'
  },
  {
    label: 'Hergeleitet',
    value:
      'Eine aus dem Namensmuster der Firma konstruierte E-Mail-Adresse, getrennt ausgewiesen und als konstruiert gekennzeichnet'
  }
];

/** The eight bands the export prints, from `SizeBandForExport`. */
export const SIZE_BANDS = '1-10 · 10-20 · 20-50 · 50-100 · 100-200 · 200-500 · 500-1000 · 1000+';

/**
 * The seven Grossregionen, with their cantons printed.
 *
 * From `RegionFilters.svelte`, which notes why the cantons are shown beside
 * each name: the grouping is then never a claim the reader has to take on
 * trust. Same reason to print them here.
 */
export const REGIONS = [
  { label: 'Zürich', value: 'ZH' },
  { label: 'Espace Mittelland', value: 'BE · FR · SO · NE · JU' },
  { label: 'Nordwestschweiz', value: 'BS · BL · AG' },
  { label: 'Zentralschweiz', value: 'LU · UR · SZ · OW · NW · ZG' },
  { label: 'Ostschweiz', value: 'GL · SH · AR · AI · SG · GR · TG' },
  { label: 'Genferseeregion', value: 'VD · VS · GE' },
  { label: 'Tessin', value: 'TI' }
];

/** The five ways a region can be expressed. */
export const REGION_MODES = [
  {
    label: 'Radius',
    value:
      'Eine Postleitzahl als Mittelpunkt und ein Umkreis in Kilometern — für ein Verkaufsgebiet, das an keiner Kantonsgrenze endet.'
  },
  {
    label: 'PLZ-Liste',
    value: 'Einzelne Postleitzahlen, wenn das Gebiet schon definiert ist.'
  },
  { label: 'Kantone', value: 'Einer oder mehrere der 26 Kantone.' },
  {
    label: 'Grossregionen',
    value: 'Die sieben Grossregionen, jede als Abkürzung für ihre Kantone.'
  },
  {
    label: 'Sprachgebiet',
    value:
      'Deutsch, Französisch, Italienisch oder Rätoromanisch — für Deutschschweiz, Romandie oder italienische Schweiz, wenn die Sprache der Ansprache das Kriterium ist.'
  }
];

/**
 * The register's 18 legal forms, in the register's own order.
 *
 * Commonest-first, not alphabetical, because that is how the register sorts
 * them — and `legalforms.ts` records that the first four are 92.4% of the
 * corpus, which is worth saying out loud rather than implying a flat list of
 * eighteen equally likely options.
 */
export const LEGAL_FORMS = [
  'Einzelunternehmen',
  'Kollektivgesellschaft',
  'AG',
  'GmbH',
  'Genossenschaft',
  'Verein',
  'Stiftung',
  'Institut des öffentlichen Rechts',
  'Zweigniederlassung',
  'Kommanditgesellschaft',
  'Zweigniederlassung einer ausl. Gesellschaft',
  'Kommanditaktiengesellschaft',
  'Besondere Rechtsform',
  'Gemeinderschaft',
  'Investmentgesellschaft mit festem Kapital',
  'Investmentgesellschaft mit variablem Kapital',
  'Kommanditgesellschaft für kollektive Kapitalanlagen',
  'Nichtkaufmännische Prokura'
];

/**
 * The 11 contact functions a customer can filter on.
 *
 * # Eleven, not the twenty-two in `de.json`
 *
 * The app's `roles` block holds 24 entries: 22 role labels and two interface
 * strings. That is not the same as what the picker offers. This list had all
 * 22 until 2026-10-21, when the Datenstamm page was seen claiming 11 above a
 * list of 22 — the count was right and the list was wrong, which is the
 * opposite of what it looked like.
 *
 * The eleven are the owner's, and the eleven that are gone are
 * Inhaberschaft, Verwaltungsratspräsidium, Assistenz der Geschäftsführung,
 * Recht, Produkt, Forschung & Entwicklung, Produktion, Kundenservice,
 * Support, Account Management and Standort. Labels existing in `de.json` is
 * not evidence they are filterable; do not restore any of them from there.
 *
 * # The order is the app's
 *
 * Not alphabetical. Geschäftsführung leads because it answers "who decides
 * here", and the departments run after it in the order the app lists them,
 * which is also the order most fallback chains get written in.
 *
 * Anything beyond these eleven is reachable through the Titelsuche instead —
 * "dazu jeder beliebige Titel" — which is why dropping them costs no reach,
 * only the ability to put them in an Ausweichreihenfolge.
 */
export const ROLES = [
  'Geschäftsführer/in',
  'Leiter/in Operations',
  'Leiter/in Administration',
  'Leiter/in Finanzen',
  'Leiter/in Buchhaltung',
  'Leiter/in Technik',
  'Leiter/in Marketing',
  'Leiter/in Vertrieb',
  'Leiter/in HR',
  'Leiter/in Logistik',
  'Leiter/in Einkauf'
];

/** How the corpus is kept current. */
export const FRESHNESS = [
  {
    label: 'Täglich aktualisiert',
    text: 'Der Bestand wird fortlaufend gepflegt und erweitert, nicht periodisch eingekauft und ersetzt.'
  },
  {
    label: 'Automatisiert gegengeprüft',
    text: 'Adressen, Telefonnummern und Zuständigkeiten werden laufend validiert; veraltete Einträge werden korrigiert statt stehen gelassen.'
  },
  {
    label: 'Verifizierte E-Mail-Adressen',
    text: 'Eine Adresse wird als Kontaktadresse geliefert, wenn die Prüfung das Postfach gefunden hat. Nimmt ein Server jede Adresse an, beweist das nichts — solche Treffer wandern nicht in die Kontaktspalte.'
  },
  {
    label: 'Konstruiertes bleibt gekennzeichnet',
    text: 'Eine aus dem Namensmuster abgeleitete Adresse steht in ihrer eigenen Spalte und wird nie als verifiziert ausgegeben.'
  }
];

/** Compliance, as facts rather than as a badge. */
/**
 * Not rendered.
 *
 * `firmendaten-schweiz/+page.svelte` imports `FOUNDATION` from this module
 * and nothing else — the page's eleven topic sections come from
 * `filter-topics.ts`. `COMPLIANCE`, `DATA_FAQ`, `SOURCES`, `RECORD_FIELDS`,
 * `SIZE_BANDS`, `REGIONS`, `REGION_MODES`, `LEGAL_FORMS` and `FRESHNESS` are
 * all dead in that sense; `ROLES` is the exception, read by `filter-topics`
 * and `filters`.
 *
 * They are kept because a later merge may want some of it back, but editing
 * them changes nothing a visitor sees. The processing-location line below was
 * brought to "Schweiz und der EU" on 2026-10-21 for consistency, not because
 * it ships.
 */
export const COMPLIANCE = [
  {
    label: 'Nur öffentlich zugängliche Quellen',
    text: 'Handelsregister, Firmenwebseiten, öffentliche Verzeichnisse und öffentliche Stellenanzeigen. Keine zugekauften Adressbestände unklarer Herkunft.'
  },
  {
    label: 'Ausschliesslich B2B',
    text: 'Erfasst werden Unternehmen und berufliche Funktionen. Privatpersonen und private Kontaktdaten sind nicht Teil des Bestands.'
  },
  {
    label: 'Verarbeitung in der Schweiz und der EU',
    text: 'Die Daten werden in Rechenzentren in der Schweiz und der EU verarbeitet und gespeichert, nach Schweizer DSG und EU-DSGVO.'
  },
  {
    label: 'Auskunft und Löschung',
    text: 'Betroffene Personen können Auskunft verlangen und die Löschung ihrer Angaben veranlassen; Anfragen laufen über die im Impressum genannte Adresse.'
  }
];

/**
 * What the corpus cannot see.
 *
 * Taken from the app's own `explain` strings and doc comments, not softened.
 */
export const LIMITS = [
  {
    label: 'Ein Flag belegt Anwesenheit, nie Abwesenheit',
    text: 'Dass eine Firma ausbildet oder Stellen ausschreibt, ist ein Beleg. Das Fehlen des Flags ist keiner: eine Firma ohne Webseite und ohne erfasste Personen lässt sich von einer, die niemanden ausbildet, nicht unterscheiden.'
  },
  {
    label: 'Nicht öffentlich heisst nicht auffindbar',
    text: 'Wer seine Stellen nicht öffentlich ausschreibt, erscheint im Stellenfilter nicht. Wer keine Webseite betreibt, hat keine Angebotszusammenfassung.'
  },
  {
    label: 'Technologien sind ein Beleg, keine Kundenliste',
    text: 'Erkannt wird, was auf der Webseite sichtbar ist. Ein Produkt kann über einen Reseller betrieben werden, dessen Infrastruktur wir stattdessen sehen. Eine «nutzt nicht»-Variante gibt es deshalb bewusst nicht.'
  },
  {
    label: 'Nur das E-Mail-Setup kennt echte Abwesenheit',
    text: 'DMARC- und SPF-Lücken sind der einzige Filter, der auf etwas Fehlendes zeigt, weil das DNS diese Frage beantworten kann. Ausgewertet wird nur, wenn die Domain auflöst.'
  },
  {
    label: 'Nicht jede Firma hat jede Funktion erfasst',
    text: 'Die 11 Funktionen sind der Rahmen, nicht eine Garantie. Deshalb lässt sich eine Ausweichreihenfolge festlegen: fehlt die erste Funktion, wird die nächste geliefert.'
  }
];

/** Data questions, in the words people ask them. */
export const DATA_FAQ = [
  {
    question: 'Woher stammen die Schweizer Firmendaten?',
    answer:
      'Aus öffentlich zugänglichen Quellen: dem Handelsregister für Firmenname, Sitz, Rechtsform, Kapital und Gründungsdatum, den Firmenwebseiten für das tatsächliche Angebot und die eingesetzten Technologien, öffentlichen Verzeichnissen für Adressen und Telefonnummern, öffentlichen Stellenanzeigen für Aktivitätssignale und dem DNS für das E-Mail-Setup einer Domain.'
  },
  {
    question: 'Wie viele Firmen und Kontakte sind erfasst?',
    answer:
      'Über 750’000 Schweizer Unternehmen und über 2.6 Millionen Kontakte, dazu 3.1 Millionen indexierte Firmenwebseiten.'
  },
  {
    question: 'Wie aktuell sind die Daten?',
    answer:
      'Der Bestand wird täglich aktualisiert und laufend gegengeprüft. Er wird gepflegt und erweitert, nicht periodisch eingekauft und ersetzt.'
  },
  {
    question: 'Welche Felder enthält ein Datensatz?',
    answer:
      '29 Spalten in fester Reihenfolge: Ainova ID, Firmenname, Sitz, Gründungsdatum, Rechtsform, Grössenklasse, Segment und Branche; die vollständige Adresse; die Erreichbarkeit der Firma über Webseite, E-Mail, Telefon, LinkedIn, Facebook, Instagram und Twitter; den Ansprechpartner mit Geschlecht, Vorname, Nachname, E-Mail, Telefon und Rolle; sowie eine separat gekennzeichnete, aus dem Namensmuster konstruierte E-Mail-Adresse.'
  },
  {
    question: 'Sind die E-Mail-Adressen verifiziert?',
    answer:
      'Adressen werden gegen den Mailserver geprüft und nur dann als Kontaktadresse geliefert, wenn das Postfach gefunden wurde. Server, die jede Adresse annehmen, beweisen nichts — solche Treffer werden nicht als verifiziert ausgegeben. Eine aus dem Namensmuster abgeleitete Adresse steht immer in ihrer eigenen, gekennzeichneten Spalte.'
  },
  {
    question: 'Nach welchen Kriterien lässt sich filtern?',
    answer:
      'Nach Beschreibung und Text, Branche, Region (Radius, PLZ-Liste, Kanton, Grossregion oder Sprachgebiet), Grösse, Firmografie aus dem Handelsregister, Erreichbarkeit, Kontaktfunktion, Aktivitätssignalen, eingesetzten Technologien und dem E-Mail-Setup — dazu die Ähnlichkeitssuche über Beispielfirmen und der Ausschluss einer eigenen Kundenliste.'
  },
  {
    question: 'Enthalten die Daten Privatpersonen?',
    answer:
      'Nein. Erfasst werden ausschliesslich Unternehmen und berufliche Funktionen. Private Kontaktdaten sind nicht Teil des Bestands.'
  },
  {
    question: 'Wo werden die Daten verarbeitet?',
    answer: 'In Rechenzentren in der Schweiz und der EU, nach Schweizer DSG und EU-DSGVO.'
  },
  {
    question: 'In welchen Formaten erhalte ich die Daten?',
    answer:
      'Als Excel-Datei (XLSX) oder als CSV, mit 29 Spalten in fester Reihenfolge. Neue Felder werden angehängt statt eingeschoben, damit eine bestehende Vorlage oder ein CRM-Mapping nach einer Erweiterung weiter funktioniert.'
  },
  {
    question: 'Was ist in den Daten nicht enthalten?',
    answer:
      'Was nicht öffentlich ist. Wer seine Stellen nicht ausschreibt, erscheint nicht im Stellenfilter; wer keine Webseite betreibt, hat keine Angebotszusammenfassung. Erkannte Technologien sind ein Beleg und keine vollständige Kundenliste, weil ein Produkt auch über einen Reseller betrieben werden kann. Und ein fehlendes Signal ist nie ein Beleg für das Gegenteil.'
  }
];
